import { DELETE, IGNORE } from 'constants/misc.const';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import lsDel from 'utils/dom/storage/lsDel.util';
import lsGet from 'utils/dom/storage/lsGet.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import { isEnvProdOnly } from 'utils/env/determine.util';
import { flagB, flagI, flagJ, flagS } from 'utils/flag.util';
import transformObject from 'utils/objects/transformObject.util';

const IS_PROD = isEnvProdOnly(); // NOTE: it means beta/alpha will be different from production

// this object will be the singleton due to it being defined at module level and exported
const FLAG = {};

const PREFIX = '__flag__'; // prevents accidental name clash in the localStorage map
const P = (key) => PREFIX + String(key);

// NOTE: short name for restricted, used often, don't want to take up space
// the fields will be removed in production due to possible unwanted side effects and/or security risks
const R = IS_PROD ? () => IGNORE : ($) => $;

const Q = Object.freeze({ quiet: true });

const on = R((key) => lsSet(P(key), true, Q));
const off = R((key) => lsSet(P(key), false, Q));
const set = R((key, val) => lsSet(P(key), val, Q));
const get = R((key) => lsGet(P(key), Q));
const del = R((key) => lsDel(P(key), Q));

const allFn = ($) => $.map(([, [k, v]]) => (k.startsWith(PREFIX) ? [k.replace(PREFIX, ''), v] : null)).filter(($) => $);
const all = R(() => transformObject(allFn, Object.entries(localStorage ?? {})));

const method = ({ name, value }) => (IS_PROD ? FLAG : Object.defineProperty(FLAG, fromAnyToTrimmed(name), { value, enumerable: false }));

const flag = ({ name, deFault, type }) => {
  const key = fromAnyToTrimmed(name);
  const convert = 'function' === typeof type ? type : flagB;

  // noinspection JSCheckFunctionSignatures
  return Object.defineProperty(FLAG, key, {
    get() {
      return IS_PROD ? deFault : convert(deFault, get(key));
    },
    set(val) {
      if (IS_PROD) return;
      if (DELETE === val) {
        del(key);
      } else {
        set(key, val);
      }
    },
    enumerable: true,
  });
};

if (!IS_PROD) {
  Object.defineProperty(FLAG, 'DELETE', { value: DELETE, enumerable: false });
}

method({ name: 'on', value: on });
method({ name: 'off', value: off });
method({ name: 'all', value: all });

method({ name: 'get', value: get });
method({ name: 'set', value: set });
method({ name: 'del', value: del });

flag({ type: flagI, name: 'checkoutPaymentPopUp', deFault: 2 });
flag({ type: flagJ, name: 'checkoutPaymentPopUpChaos', deFault: null });

flag({ type: flagB, name: 'expandDayOffHours', deFault: true });
flag({ type: flagB, name: 'mediaQ', deFault: false });
flag({ type: flagB, name: 'multiplePosReadersDialog', deFault: false });
flag({ type: flagI, name: 'overrideFetchTimeout', deFault: 0 });
flag({ type: flagS, name: 'overrideLogLevel', deFault: '' });
flag({ type: flagB, name: 'pinCode', deFault: false });
flag({ type: flagB, name: 'pusherPosReaderDialog', deFault: false });
flag({ type: flagJ, name: 'reactScanOptions', deFault: null });
flag({ type: flagB, name: 'reactStrictMode', deFault: false });
flag({ type: flagB, name: 'sentryReplay', deFault: false });
flag({ type: flagB, name: 'stripeCapital', deFault: false });

flag({ type: flagB, name: 'tabTargets', deFault: false });
flag({ type: flagB, name: 'trackBeCalls', deFault: false });

flag({ type: flagB, name: 'updateColumnsAlways', deFault: false });
flag({ type: flagB, name: 'updateColumnsLogNegatives', deFault: false });

flag({ type: flagB, name: 'gilt', deFault: false });
flag({ type: flagB, name: 'loggerAppointmentMachine', deFault: false });
flag({ type: flagB, name: 'loggerBeVersion1', deFault: false });
flag({ type: flagB, name: 'loggerButton', deFault: false });
flag({ type: flagS, name: 'loggerMachineX', deFault: '' });
flag({ type: flagB, name: 'loggerMemoizedColumn', deFault: false });
flag({ type: flagB, name: 'loggerSalonModalMount', deFault: false });
flag({ type: flagB, name: 'loggerSpam', deFault: false });
flag({ type: flagB, name: 'loggerStore', deFault: false });
flag({ type: flagB, name: 'loggerWrap', deFault: false });

flag({ type: flagB, name: 'pusherLogStateChange', deFault: false });
flag({ type: flagB, name: 'pusherLogToConsole', deFault: false });

flag({ type: flagB, name: 'reactQueryAbortPromise', deFault: false });
flag({ type: flagB, name: 'reactQueryAbortSignal', deFault: false });
flag({ type: flagB, name: 'reactQueryDevtools', deFault: false });
flag({ type: flagB, name: 'reactQueryForcedStale', deFault: false });

// the code that actually deletes properties marked with the symbol IGNORE (to avoid using the value undefined)
for (const [key, val] of Object.entries(FLAG)) if (val === IGNORE) delete FLAG[key];

// noinspection JSUnusedGlobalSymbols
export default FLAG;
